import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useMutation } from 'react-query';
import apiClient from '../../request/http-common';

const CreateJointAccount = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    account_name: '',
    description: '',
    minimum_approval_amount: 100,
    approval_type: 'majority',
    members: [{ email: '', role: 'member' }]
  });
  const [errors, setErrors] = useState({});

  const { mutate: createAccount, isLoading } = useMutation(
    async (data) => {
      const response = await apiClient.post('/api/joint-accounts', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        onSuccess();
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          setErrors({ general: error.response?.data?.message || 'Failed to create joint account' });
        }
      }
    }
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleMemberChange = (index, field, value) => {
    const newMembers = [...formData.members];
    newMembers[index] = { ...newMembers[index], [field]: value };
    setFormData(prev => ({ ...prev, members: newMembers }));
    
    if (errors[`members.${index}.${field}`]) {
      setErrors(prev => ({ ...prev, [`members.${index}.${field}`]: null }));
    }
  };

  const addMember = () => {
    setFormData(prev => ({
      ...prev,
      members: [...prev.members, { email: '', role: 'member' }]
    }));
  };

  const removeMember = (index) => {
    if (formData.members.length > 1) {
      setFormData(prev => ({
        ...prev,
        members: prev.members.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    createAccount(formData);
  };

  const approvalTypeDescriptions = {
    any_member: 'Any single member can approve transactions',
    majority: 'Majority of members must approve transactions',
    all_members: 'All members must approve transactions',
    specific_amount: 'Requires 2+ approvals for large amounts'
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      {errors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.general}
        </Alert>
      )}

      {/* Account Information */}
      <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
        Account Information
      </Typography>

      <TextField
        fullWidth
        label="Account Name"
        value={formData.account_name}
        onChange={(e) => handleInputChange('account_name', e.target.value)}
        error={!!errors.account_name}
        helperText={errors.account_name}
        sx={{ mb: 2 }}
        InputProps={{
          style: { color: '#FFFFFF' }
        }}
        InputLabelProps={{
          style: { color: '#6C7293' }
        }}
      />

      <TextField
        fullWidth
        label="Description (Optional)"
        multiline
        rows={3}
        value={formData.description}
        onChange={(e) => handleInputChange('description', e.target.value)}
        error={!!errors.description}
        helperText={errors.description}
        sx={{ mb: 2 }}
        InputProps={{
          style: { color: '#FFFFFF' }
        }}
        InputLabelProps={{
          style: { color: '#6C7293' }
        }}
      />

      <TextField
        fullWidth
        label="Minimum Approval Amount ($)"
        type="number"
        value={formData.minimum_approval_amount}
        onChange={(e) => handleInputChange('minimum_approval_amount', parseFloat(e.target.value))}
        error={!!errors.minimum_approval_amount}
        helperText={errors.minimum_approval_amount || 'Transactions above this amount require approval'}
        sx={{ mb: 2 }}
        InputProps={{
          style: { color: '#FFFFFF' }
        }}
        InputLabelProps={{
          style: { color: '#6C7293' }
        }}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: '#6C7293' }}>Approval Type</InputLabel>
        <Select
          value={formData.approval_type}
          onChange={(e) => handleInputChange('approval_type', e.target.value)}
          error={!!errors.approval_type}
          sx={{ color: '#FFFFFF' }}
        >
          <MenuItem value="any_member">Any Member</MenuItem>
          <MenuItem value="majority">Majority</MenuItem>
          <MenuItem value="all_members">All Members</MenuItem>
          <MenuItem value="specific_amount">Specific Amount</MenuItem>
        </Select>
        <Typography variant="caption" sx={{ color: '#6C7293', mt: 1 }}>
          {approvalTypeDescriptions[formData.approval_type]}
        </Typography>
      </FormControl>

      <Divider sx={{ my: 3, borderColor: '#2B3139' }} />

      {/* Members Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 2 
      }}>
        <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
          Invite Members
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={addMember}
          variant="outlined"
          size="small"
          sx={{
            borderColor: '#F7931A',
            color: '#F7931A',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              borderColor: '#FFB800',
              backgroundColor: 'rgba(247, 147, 26, 0.1)'
            }
          }}
        >
          Add Member
        </Button>
      </Box>

      {formData.members.map((member, index) => (
        <Box key={index} sx={{ 
          mb: 2, 
          p: { xs: 1.5, sm: 2 }, 
          border: '1px solid #2B3139', 
          borderRadius: '8px' 
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 2,
            flexWrap: 'wrap'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <PersonIcon sx={{ color: '#6C7293' }} />
              <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>
                Member {index + 1}
              </Typography>
            </Box>
            {formData.members.length > 1 && (
              <IconButton
                onClick={() => removeMember(index)}
                size="small"
                sx={{ color: '#F84960' }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2 
          }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={member.email}
              onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
              error={!!errors[`members.${index}.email`]}
              helperText={errors[`members.${index}.email`]}
              InputProps={{
                style: { color: '#FFFFFF' }
              }}
              InputLabelProps={{
                style: { color: '#6C7293' }
              }}
            />

            <FormControl sx={{ 
              minWidth: { xs: '100%', sm: 120 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <InputLabel sx={{ color: '#6C7293' }}>Role</InputLabel>
              <Select
                value={member.role}
                onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                error={!!errors[`members.${index}.role`]}
                sx={{ color: '#FFFFFF' }}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      ))}

      <Typography variant="caption" sx={{ 
        color: '#6C7293', 
        display: 'block', 
        mb: 3,
        fontSize: { xs: '0.7rem', sm: '0.75rem' },
        lineHeight: 1.4
      }}>
        • Admin: Can approve transactions and manage members
        • Member: Can initiate transactions and view account  
        • Viewer: Can only view transactions and balance
      </Typography>

      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2, 
        justifyContent: 'flex-end', 
        mt: 4,
        mb: { xs: 8, sm: 0 }
      }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderColor: '#6C7293',
            color: '#6C7293',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              borderColor: '#FFFFFF',
              color: '#FFFFFF'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          sx={{
            background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              background: 'linear-gradient(135deg, #E8851A 0%, #E8A800 100%)'
            },
            '&:disabled': {
              background: '#2B3139'
            }
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: '#FFFFFF' }} />
              Creating...
            </>
          ) : (
            'Create Joint Account'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateJointAccount; 